import { useState } from 'react';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import type { Organization } from '@/services/api';

interface OrganizationTabProps {
  org: Organization;
  userCount: number;
  domainInput: string;
  domainSaving: boolean;
  domainSaved: boolean;
  domainError: string;
  onDomainChange: (value: string) => void;
  onDomainSave: () => void;
}

export function OrganizationTab({
  org,
  userCount,
  domainInput,
  domainSaving,
  domainSaved,
  domainError,
  onDomainChange,
  onDomainSave,
}: OrganizationTabProps) {
  const [refCopied, setRefCopied] = useState(false);
  const referralUrl = `https://apps.tabillo.com/signup?ref=${org.slug}`;

  const handleCopyReferral = () => {
    navigator.clipboard.writeText(referralUrl).then(() => {
      setRefCopied(true);
      setTimeout(() => setRefCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-text-primary">Organization</h2>

      {/* Info grid */}
      <div className="bg-surface-default rounded-lg border border-border-default p-6 space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Name</label>
            <p className="text-sm text-text-primary">{org.name}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">URL</label>
            <p className="text-sm text-text-primary">{org.slug}.tabillo.com</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Plan</label>
            <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
              org.plan === 'pro' ? 'bg-blue-100 text-blue-700'
                : org.plan === 'enterprise' ? 'bg-purple-100 text-purple-700'
                : 'bg-green-100 text-green-700'
            }`}>
              {org.plan === 'free' ? 'Free' : org.plan.charAt(0).toUpperCase() + org.plan.slice(1)}
            </span>
            {org.plan === 'free' && (
              <p className="text-xs text-text-tertiary mt-1">Limited period · subject to change</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Status</label>
            <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
              org.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {org.status.charAt(0).toUpperCase() + org.status.slice(1)}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Created</label>
            <p className="text-sm text-text-primary">
              {new Date(org.createdAt).toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric',
              })}
            </p>
          </div>
          {org.trialExpiresAt && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Trial Expires</label>
              <p className="text-sm text-text-primary">
                {new Date(org.trialExpiresAt).toLocaleDateString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric',
                })}
              </p>
            </div>
          )}
        </div>

        {/* Referral link */}
        <div className="pt-4 border-t border-border-default">
          <label className="block text-sm font-medium text-text-secondary mb-1">Referral Link</label>
          <p className="text-xs text-text-tertiary mb-2">Share this link — you earn +20 AI credits, they earn +10 when they sign up.</p>
          <div className="flex items-center gap-2">
            <span className="flex-1 px-3 py-2 text-xs bg-surface-subtle border border-border-default rounded-md text-text-secondary font-mono truncate">
              {referralUrl}
            </span>
            <button
              onClick={handleCopyReferral}
              className="flex items-center gap-1.5 px-3 py-2 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-medium rounded-md transition-colors shrink-0"
            >
              {refCopied ? (
                <><CheckIcon className="w-3.5 h-3.5" />Copied</>
              ) : (
                <><ClipboardDocumentIcon className="w-3.5 h-3.5" />Copy</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Allowed Email Domains */}
      <div className="bg-surface-default rounded-lg border border-border-default p-6">
        <h3 className="text-sm font-semibold text-text-primary mb-3">Allowed Email Domains</h3>
        <p className="text-xs text-text-secondary mb-3">
          Only users with these email domains can log in. Leave empty to allow any email domain.
        </p>
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={domainInput}
              onChange={(e) => onDomainChange(e.target.value)}
              placeholder="e.g. microsoft.com, aws.com"
              className="w-full px-3 py-2 text-sm border border-border-default rounded-md bg-surface-default text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-text-secondary mt-1.5">Separate multiple domains with commas</p>
          </div>
          <button
            onClick={onDomainSave}
            disabled={domainSaving || domainSaved}
            className={`px-4 py-2 text-white text-sm font-medium rounded-md disabled:opacity-50 whitespace-nowrap ${
              domainSaved ? 'bg-green-600' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {domainSaving ? 'Saving...' : domainSaved ? 'Saved!' : 'Save'}
          </button>
        </div>
        {domainError && <p className="text-xs text-red-600 mt-2">{domainError}</p>}
      </div>

      {/* Usage Limits */}
      <div className="bg-surface-default rounded-lg border border-border-default p-6">
        <h3 className="text-sm font-semibold text-text-primary mb-3">Usage Limits</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-xs text-text-secondary mb-1">Max Users</label>
            <p className="text-lg font-semibold text-text-primary">
              {userCount} <span className="text-sm font-normal text-text-secondary">/ {org.maxUsers}</span>
            </p>
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">Max Initiatives</label>
            <p className="text-lg font-semibold text-text-primary">
              {org.maxInitiatives === null
                ? <span className="text-text-secondary font-normal text-sm">Unlimited</span>
                : org.maxInitiatives}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
